import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Mail, RotateCw, ShieldCheck, Trash2, UserPlus } from "lucide-react";
import * as React from "react";
import { toast } from "sonner";

import {
  AlertaErro,
  AlertaOk,
  Campo,
  mensagemDeErro,
  problemaSenha,
  SENHA_MINIMA,
} from "@/components/acesso/LayoutAcesso";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { api, sessao } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import type { Papel, Usuario } from "@/types";

const PAPEIS: { valor: Papel; rotulo: string; descricao: string }[] = [
  { valor: "admin", rotulo: "Admin", descricao: "tudo, inclusive gerir usuários e a aprovação de Negócio" },
  { valor: "editor", rotulo: "Editor", descricao: "cria e altera operações" },
  { valor: "leitor", rotulo: "Leitor", descricao: "só consulta" },
  { valor: "tax", rotulo: "Tax", descricao: "time TAX: aprova as operações do ponto de vista fiscal" },
  { valor: "contabil", rotulo: "Contábil", descricao: "time contábil: aprova as operações do ponto de vista contábil" },
];

/** "Minha conta": senha, e-mail e, para o admin, a gestão de usuários. */
export function ContaDialog({
  aberto,
  aoMudar,
}: {
  aberto: boolean;
  aoMudar: (aberto: boolean) => void;
}) {
  const { ehAdmin } = useAuth();
  return (
    <Dialog open={aberto} onOpenChange={aoMudar}>
      <DialogContent className="w-[min(46rem,94vw)]">
        <DialogHeader>
          <DialogTitle>Minha conta</DialogTitle>
          <DialogDescription>Altere sua senha ou o e-mail de acesso.</DialogDescription>
        </DialogHeader>
        <Tabs defaultValue="senha">
          <TabsList>
            <TabsTrigger value="senha">Senha</TabsTrigger>
            <TabsTrigger value="email">E-mail</TabsTrigger>
            {ehAdmin && <TabsTrigger value="usuarios">Usuários</TabsTrigger>}
          </TabsList>
          <TabsContent value="senha">
            <AbaSenha />
          </TabsContent>
          <TabsContent value="email">
            <AbaEmail />
          </TabsContent>
          {ehAdmin && (
            <TabsContent value="usuarios">
              <AbaUsuarios />
            </TabsContent>
          )}
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}

// ------------------------------------------------------------------ senha

function AbaSenha() {
  const [atual, setAtual] = React.useState("");
  const [nova, setNova] = React.useState("");
  const [confirmacao, setConfirmacao] = React.useState("");
  const [erro, setErro] = React.useState("");

  const trocar = useMutation({
    mutationFn: () =>
      api<{ accessToken: string; refreshToken: string }>("/auth/senha", {
        method: "POST",
        body: { senhaAtual: atual, senhaNova: nova },
      }),
    onSuccess: (r) => {
      // As sessões antigas caem no servidor; esta segue com os tokens novos.
      sessao.definir(r.accessToken, r.refreshToken);
      setAtual("");
      setNova("");
      setConfirmacao("");
      toast.success("Senha alterada. As outras sessões abertas foram encerradas.");
    },
    onError: (falha) => setErro(mensagemDeErro(falha, "Não foi possível trocar a senha.")),
  });

  const submeter = (evento: React.FormEvent) => {
    evento.preventDefault();
    const problema = problemaSenha(nova, confirmacao);
    setErro(problema);
    if (!problema) trocar.mutate();
  };

  return (
    <form className="grid max-w-sm gap-4" onSubmit={submeter}>
      <Campo
        id="conta-senha-atual"
        rotulo="Senha atual"
        type="password"
        autoComplete="current-password"
        required
        value={atual}
        onChange={(e) => setAtual(e.target.value)}
      />
      <Campo
        id="conta-senha-nova"
        rotulo={`Nova senha (mínimo de ${SENHA_MINIMA} caracteres)`}
        type="password"
        autoComplete="new-password"
        required
        value={nova}
        onChange={(e) => setNova(e.target.value)}
      />
      <Campo
        id="conta-senha-confirmacao"
        rotulo="Confirme a nova senha"
        type="password"
        autoComplete="new-password"
        required
        value={confirmacao}
        onChange={(e) => setConfirmacao(e.target.value)}
      />
      {erro && <AlertaErro>{erro}</AlertaErro>}
      <div>
        <Button type="submit" disabled={trocar.isPending}>
          {trocar.isPending ? "Salvando…" : "Alterar senha"}
        </Button>
      </div>
    </form>
  );
}

// ------------------------------------------------------------------ e-mail

function AbaEmail() {
  const { usuario } = useAuth();
  const [novo, setNovo] = React.useState("");
  const [senha, setSenha] = React.useState("");
  const [erro, setErro] = React.useState("");
  const [enviadoPara, setEnviadoPara] = React.useState("");

  const pedir = useMutation({
    mutationFn: () =>
      api<{ emailNovo: string }>("/auth/email", {
        method: "POST",
        body: { emailNovo: novo, senhaAtual: senha },
      }),
    onSuccess: (r) => {
      setEnviadoPara(r.emailNovo);
      setNovo("");
      setSenha("");
    },
    onError: (falha) => setErro(mensagemDeErro(falha, "Não foi possível pedir a troca.")),
  });

  const submeter = (evento: React.FormEvent) => {
    evento.preventDefault();
    setErro("");
    setEnviadoPara("");
    pedir.mutate();
  };

  return (
    <form className="grid max-w-sm gap-4" onSubmit={submeter}>
      <div>
        <Label>E-mail atual</Label>
        <p className="mt-1 text-sm font-bold text-fs-escuro">{usuario?.email}</p>
      </div>
      <Campo
        id="conta-email-novo"
        rotulo="Novo e-mail"
        type="email"
        autoComplete="email"
        required
        value={novo}
        onChange={(e) => setNovo(e.target.value)}
      />
      <Campo
        id="conta-email-senha"
        rotulo="Senha atual"
        type="password"
        autoComplete="current-password"
        required
        value={senha}
        onChange={(e) => setSenha(e.target.value)}
      />
      {erro && <AlertaErro>{erro}</AlertaErro>}
      {enviadoPara && (
        <AlertaOk>
          Enviamos um link de confirmação para {enviadoPara}. A troca só vale depois que você
          clicar nele. Até lá, continue entrando com o e-mail atual.
        </AlertaOk>
      )}
      <div>
        <Button type="submit" disabled={pedir.isPending}>
          <Mail />
          {pedir.isPending ? "Enviando…" : "Enviar confirmação"}
        </Button>
      </div>
    </form>
  );
}

// ------------------------------------------------------------------ usuários (admin)

const CHAVE_USUARIOS = ["auth", "usuarios"];

function AbaUsuarios() {
  const { usuario: eu } = useAuth();
  const queryClient = useQueryClient();
  const usuarios = useQuery({
    queryKey: CHAVE_USUARIOS,
    queryFn: () => api<Usuario[]>("/auth/usuarios"),
  });
  const recarregar = () => queryClient.invalidateQueries({ queryKey: CHAVE_USUARIOS });
  const [confirmandoRemocao, setConfirmandoRemocao] = React.useState<string | null>(null);

  const mudarPapel = useMutation({
    mutationFn: ({ id, papel }: { id: string; papel: Papel }) =>
      api<Usuario>(`/auth/usuarios/${id}`, { method: "PATCH", body: { papel } }),
    onSuccess: (u) => {
      toast.success(`${u.nome} agora é ${u.papel}.`);
      recarregar();
    },
    onError: (falha) => toast.error(mensagemDeErro(falha, "Não foi possível alterar o papel.")),
  });

  const remover = useMutation({
    mutationFn: (u: Usuario) => api(`/auth/usuarios/${u.id}`, { method: "DELETE" }),
    onSuccess: (_r, u) => {
      toast.success(`${u.nome} foi removido.`);
      setConfirmandoRemocao(null);
      recarregar();
    },
    onError: (falha) => toast.error(mensagemDeErro(falha, "Não foi possível remover.")),
  });

  const reenviar = useMutation({
    mutationFn: (u: Usuario) =>
      api<{ emailEnviado: boolean }>(`/auth/usuarios/${u.id}/convite`, { method: "POST" }),
    onSuccess: (r, u) =>
      r.emailEnviado
        ? toast.success(`Convite reenviado para ${u.email}.`)
        : toast.error("O convite foi gerado, mas o e-mail não saiu. Confira o SMTP2GO."),
    onError: (falha) => toast.error(mensagemDeErro(falha, "Não foi possível reenviar.")),
  });

  return (
    <div className="grid gap-6">
      <FormConvite aoConvidar={recarregar} />

      <div>
        <h3 className="mb-2 text-sm font-extrabold text-fs-escuro">Usuários com acesso</h3>
        {usuarios.isLoading ? (
          <p className="text-sm text-muted-foreground">Carregando…</p>
        ) : usuarios.isError ? (
          <AlertaErro>{mensagemDeErro(usuarios.error, "Não foi possível listar.")}</AlertaErro>
        ) : (
          <div className="overflow-x-auto rounded-xl border border-border/80">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Usuário</TableHead>
                  <TableHead className="w-36">Papel</TableHead>
                  <TableHead className="w-44 text-right">Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {usuarios.data?.map((u) => {
                  const souEu = u.id === eu?.id;
                  return (
                    <TableRow key={u.id}>
                      <TableCell>
                        <div className="flex flex-wrap items-center gap-1.5">
                          <span className="font-bold text-fs-escuro">{u.nome}</span>
                          {souEu && <Badge variant="neutro">você</Badge>}
                          {u.pendente && <Badge variant="atencao">convite pendente</Badge>}
                        </div>
                        <div className="text-xs text-muted-foreground">{u.email}</div>
                      </TableCell>
                      <TableCell>
                        <Select
                          value={u.papel}
                          disabled={souEu || mudarPapel.isPending}
                          onValueChange={(papel) => mudarPapel.mutate({ id: u.id, papel: papel as Papel })}
                        >
                          <SelectTrigger className="h-8" aria-label={`Papel de ${u.nome}`}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {PAPEIS.map((p) => (
                              <SelectItem key={p.valor} value={p.valor}>
                                {p.rotulo}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-wrap justify-end gap-1">
                          {!souEu && u.papel !== "admin" && (
                            <Button
                              size="sm"
                              variant="ghost"
                              title="Promover a admin"
                              disabled={mudarPapel.isPending}
                              onClick={() => mudarPapel.mutate({ id: u.id, papel: "admin" })}
                            >
                              <ShieldCheck />
                              <span className="sr-only">Promover a admin</span>
                            </Button>
                          )}
                          {u.pendente && (
                            <Button
                              size="sm"
                              variant="ghost"
                              title="Reenviar convite"
                              disabled={reenviar.isPending}
                              onClick={() => reenviar.mutate(u)}
                            >
                              <RotateCw />
                              <span className="sr-only">Reenviar convite</span>
                            </Button>
                          )}
                          {!souEu &&
                            (confirmandoRemocao === u.id ? (
                              <>
                                <Button
                                  size="sm"
                                  variant="destructive"
                                  disabled={remover.isPending}
                                  onClick={() => remover.mutate(u)}
                                >
                                  Remover
                                </Button>
                                <Button size="sm" variant="ghost" onClick={() => setConfirmandoRemocao(null)}>
                                  Cancelar
                                </Button>
                              </>
                            ) : (
                              <Button
                                size="sm"
                                variant="ghost"
                                title="Remover usuário"
                                className="text-destructive hover:bg-destructive/10 hover:text-destructive"
                                onClick={() => setConfirmandoRemocao(u.id)}
                              >
                                <Trash2 />
                                <span className="sr-only">Remover usuário</span>
                              </Button>
                            ))}
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        )}
        <p className="mt-2 text-[11px] leading-relaxed text-muted-foreground">
          Remover encerra o acesso na hora, mas mantém o histórico de autoria. Um usuário removido
          pode ser convidado de novo pelo mesmo e-mail.
        </p>
      </div>
    </div>
  );
}

function FormConvite({ aoConvidar }: { aoConvidar: () => void }) {
  const [nome, setNome] = React.useState("");
  const [email, setEmail] = React.useState("");
  const [papel, setPapel] = React.useState<Papel>("editor");
  const [erro, setErro] = React.useState("");

  const convidar = useMutation({
    mutationFn: () =>
      api<{ usuario: Usuario; emailEnviado: boolean }>("/auth/usuarios", {
        method: "POST",
        body: { nome, email, papel },
      }),
    onSuccess: (r) => {
      if (r.emailEnviado) toast.success(`Convite enviado para ${r.usuario.email}.`);
      else
        toast.error(
          "Usuário criado, mas o e-mail de convite não saiu. Confira o SMTP2GO e use “Reenviar convite”.",
        );
      setNome("");
      setEmail("");
      setPapel("editor");
      aoConvidar();
    },
    onError: (falha) => setErro(mensagemDeErro(falha, "Não foi possível convidar.")),
  });

  const submeter = (evento: React.FormEvent) => {
    evento.preventDefault();
    setErro("");
    convidar.mutate();
  };

  return (
    <form
      className="grid gap-3 rounded-xl border border-border/80 bg-secondary/40 p-4"
      onSubmit={submeter}
    >
      <div>
        <h3 className="text-sm font-extrabold text-fs-escuro">Convidar usuário</h3>
        <p className="text-xs text-muted-foreground">
          A pessoa recebe um e-mail de boas-vindas para escolher a senha e concluir o cadastro.
        </p>
      </div>
      <div className="grid gap-3 sm:grid-cols-[1fr_1fr_9rem]">
        <Campo
          id="convite-nome"
          rotulo="Nome"
          required
          maxLength={120}
          value={nome}
          onChange={(e) => setNome(e.target.value)}
        />
        <Campo
          id="convite-email"
          rotulo="E-mail"
          type="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />
        <div>
          <Label htmlFor="convite-papel">Papel</Label>
          <Select value={papel} onValueChange={(v) => setPapel(v as Papel)}>
            <SelectTrigger id="convite-papel">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {PAPEIS.map((p) => (
                <SelectItem key={p.valor} value={p.valor}>
                  {p.rotulo}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
      <p className="text-[11px] text-muted-foreground">
        {PAPEIS.find((p) => p.valor === papel)?.rotulo}: {PAPEIS.find((p) => p.valor === papel)?.descricao}.
      </p>
      {erro && <AlertaErro>{erro}</AlertaErro>}
      <div>
        <Button type="submit" disabled={convidar.isPending}>
          <UserPlus />
          {convidar.isPending ? "Enviando convite…" : "Enviar convite"}
        </Button>
      </div>
    </form>
  );
}
